import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { storesAPI } from '../services/api';
import { mensajeDeError } from '../utils/apiError';
import { notify } from '../utils/notify';
import { useTranslation } from 'react-i18next';

/**
 * Vuelta desde eBay. eBay redirige aquí con ?code=... y lo cambiamos por los
 * tokens reales, que es lo único que permite publicar productos.
 *
 * El resultado se comunica con el **modal estándar** de la aplicación
 * (`notify`), el mismo que usan Shopify y el login, en lugar de una pantalla a
 * medida: así todos los avisos de «tienda conectada» se ven igual. Tras avisar,
 * se vuelve a /stores; el modal queda montado a nivel de app, así que se ve por
 * encima de la lista de tiendas.
 */
export default function EbayCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const yaLanzado = useRef(false);

    useEffect(() => {
        if (yaLanzado.current) return;
        yaLanzado.current = true;

        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorDeEbay = searchParams.get('error_description') || searchParams.get('error');

        // Cualquiera que sea el desenlace, el aviso sale en el modal estándar y
        // se vuelve a la lista de tiendas (con `replace` para que el botón
        // «atrás» no reintente el canje).
        const volverATiendas = () => navigate('/stores', { replace: true });

        // El texto de eBay llega en inglés y a veces es un código
        // (`access_denied`): va dentro de una frase traducida, no solo, para
        // que el aviso se entienda en los dos idiomas sin perder el motivo.
        if (errorDeEbay) {
            notify.error(t('stores.ebay_connect_failed_reason', { motivo: errorDeEbay }));
            volverATiendas();
            return;
        }

        // eBay devuelve `ebaytkn` cuando el RuName está configurado como
        // Auth'n'Auth, el flujo antiguo. Ese token no sirve para publicar: no da
        // acceso a la Inventory API ni trae refresh token. Decirlo por su nombre
        // ahorra media hora de búsqueda a ciegas.
        if (!code && searchParams.has('ebaytkn')) {
            notify.error(t('stores.ebay_wrong_flow'));
            volverATiendas();
            return;
        }

        if (!code) {
            notify.error(t('stores.ebay_no_code'));
            volverATiendas();
            return;
        }

        storesAPI
            .ebayExchange({ code, state, storeName: t('stores.ebay_default_name') })
            .then(() => {
                notify.success(t('stores.ebay_connected'));
            })
            .catch((err) => {
                notify.error(mensajeDeError(err, t('stores.ebay_connect_failed')));
            })
            .finally(volverATiendas);
    }, [searchParams, navigate, t]);

    // Mientras se canjea el código solo se ve un spinner; el resultado (éxito o
    // error) se comunica con el modal estándar ya en la pantalla de Tiendas.
    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-900">
            <div className="card max-w-md w-full text-center space-y-4">
                <div className="w-12 h-12 mx-auto border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-slate-600 dark:text-slate-300">{t('stores.ebay_connecting')}</p>
            </div>
        </div>
    );
}
